import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { transactionsService } from "@/app/services/transactionService";
import { AttachmentType } from "@/app/services/transactionService/attachmentsType";

import { useCrefazContext } from "@/app/contexts/CrefazContext";
import { useToast } from "@/view/components/ui/use-toast";

const listDocumentsSchema = z.object({});

type ListDocumentsFormData = z.infer<typeof listDocumentsSchema>;

export interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  side?: "frente" | "verso";
}

export interface DocumentType {
  id: string;
  title: string;
  description?: string;
  files: UploadedFile[];
  required: boolean;
  maxFiles: number;
  isIdDocument: boolean;
  isEnergyBill: boolean;
}

interface UseListDocumentsModalProps {
  onContinue: () => void;
}

export default function useListDocumentsModal({
  onContinue,
}: UseListDocumentsModalProps) {
  const { transactionId } = useCrefazContext();
  const { toast } = useToast();

  const form = useForm<ListDocumentsFormData>({
    resolver: zodResolver(listDocumentsSchema),
  });

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const { data: attachmentsType } = useQuery<AttachmentType[]>({
    queryKey: ["attachments-type", transactionId],
    queryFn: () => transactionsService.attachmentsType(transactionId!),
    enabled: !!transactionId,
  });

  const [documents, setDocuments] = useState<DocumentType[]>([]);

  useEffect(() => {
    if (attachmentsType) {
      setDocuments(() => {
        return attachmentsType.map((att) => {
          const isId = att.nome.toUpperCase() === "DOCUMENTO DE IDENTIFICAÇÃO";
          const isEnergyBill = att.nome.toUpperCase() === "FATURA DE ENERGIA";

          return {
            id: att.documentId.toString(),
            title: att.nome,
            description: isId
              ? "RG, CNH ou Passaporte"
              : "Selecione um documento",
            files: [],
            required: true,
            maxFiles: isId ? 2 : 1,
            isIdDocument: isId,
            isEnergyBill,
          };
        });
      });
    } else if (!transactionId) {
      setDocuments([
        {
          id: "energy_bill",
          title: "Fatura de Energia",
          description: "Selecione um documento",
          files: [],
          required: true,
          maxFiles: 1,
          isIdDocument: false,
          isEnergyBill: true,
        },
        {
          id: "id_document",
          title: "Documento de identificação",
          description: "RG, CNH ou Passaporte",
          files: [],
          required: true,
          maxFiles: 2,
          isIdDocument: true,
          isEnergyBill: false,
        },
      ]);
    }
  }, [attachmentsType, transactionId]);

  const handleAddFile = (
    documentId: string,
    file: File,
    side?: "frente" | "verso",
  ) => {
    const newFile: UploadedFile = {
      id: crypto.randomUUID(),
      file,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
      side,
    };

    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id !== documentId) return doc;

        if (side) {
          const existingFiles = doc.files.filter((f) => f.side !== side);
          return { ...doc, files: [...existingFiles, newFile] };
        }

        return { ...doc, files: [...doc.files, newFile] };
      }),
    );
  };

  const handleRemoveFile = (documentId: string, fileId: string) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === documentId
          ? { ...doc, files: doc.files.filter((f) => f.id !== fileId) }
          : doc,
      ),
    );
  };

  const handleSelectDocument = (id: string) => {
    setSelectedDocumentId(id);
  };

  const handleBackToList = () => {
    setSelectedDocumentId(null);
  };

  const [isLoading, setIsLoading] = useState(false);

  const submitModal = form.handleSubmit(async () => {
    const missingRequired = documents.some(
      (doc) => doc.required && doc.files.length === 0,
    );

    if (missingRequired) {
      console.log("Missing required documents");
      return;
    }

    if (transactionId) {
      setIsLoading(true);
      try {
        for (const doc of documents) {
          for (const file of doc.files) {
            await transactionsService.uploadAttachment(
              transactionId,
              doc.id,
              file.file,
            );
          }
        }
      } catch (error: any) {
        console.error("Error uploading files", error);
        toast({
          title: "Erro ao enviar documento",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Erro inesperado ao enviar o documento. Tente novamente.",
          variant: "destructive",
        });
        return;
      } finally {
        setIsLoading(false);
      }
    }

    onContinue();
  });

  return {
    form,
    documents,
    handleAddFile,
    handleRemoveFile,
    submitModal,
    selectedDocumentId,
    handleSelectDocument,
    handleBackToList,
    setSelectedDocumentId,
    isLoading,
  };
}
